import { Card, Flex, Select, Tabs } from "antd";
import { isAxiosError } from "axios";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import type {
  CommentAttachmentTarget,
  CommentAuthor,
  CommentEntry,
  CommentNewFiles,
  ReactionMutationResult,
  TaskSummary,
} from "../../../common/components/CommentsTab";
import { CommentsTab } from "../../../common/components/CommentsTab";
import { UNPAGED_LIMIT } from "../../../common/constants/constants";
import { buildWOClipboardContext } from "../../../common/utils/buildWOClipboardContext";
import { formatComponentLabel } from "../../../common/utils/componentLabel";
import { useDeepLinkScroll } from "../../../common/utils/useDeepLinkScroll";
import { client } from "../../api-client";
import {
  attachFileToComment,
  commentAttachmentsLinked,
  uploadCommentAttachment,
} from "../../files/store";
import type { EmojiEnum } from "../../generated-axios-client";
import type { RootState } from "../../store";
import { buildLogContext } from "../worklog/workLogFormatter";
import { buildDownloadUrl, FilesInfo } from "./FilesInfo";
import { StandbyPeriodsInfo } from "./StandbyPeriodsInfo";
import { SuppliesInfo } from "./SuppliesInfo";
import { TaskLogInfo } from "./TaskLogInfo";
import { TasksInfo } from "./TasksInfo";
import { TicketInfo } from "./Ticket";
import type { TicketViewRenderProps } from "./TicketViewRender";
import { TimeSessionsInfo } from "./TimeSessionsInfo";
import { WorkLogInfo } from "./WorkLogInfo";

const DEEP_LINK_TAB_BY_SECTION: Record<string, string> = {
  files: "files",
  comments: "comments",
};

export const TicketInformation = (props: TicketViewRenderProps) => {
  const { intl, currentRoute, ticketData, modalApi, dispatch } = props;
  const { ticket, files } = ticketData;

  const linkedSection = currentRoute?.params?.section as string | undefined;
  const linkedTaskId = currentRoute?.params?.taskId as string | undefined;
  const linkedTab = linkedSection ? DEEP_LINK_TAB_BY_SECTION[linkedSection] : undefined;

  const currentUserId = useSelector((state: RootState) => state.userstate.user?.id);
  const [selectedTaskId, setSelectedTaskId] = useState<string | undefined>(undefined);
  const [activeTab, setActiveTab] = useState<string>(linkedTab ?? "comments");
  const tabsCardRef = useRef<HTMLDivElement>(null);

  useDeepLinkScroll(tabsCardRef, linkedTab ? `${linkedTab}:${linkedTaskId ?? ""}` : null);

  useEffect(() => {
    if (linkedTab) setActiveTab(linkedTab);
  }, [linkedTab]);

  useEffect(() => {
    if (linkedSection === "comments" && linkedTaskId) setSelectedTaskId(linkedTaskId);
  }, [linkedSection, linkedTaskId]);

  const commentList = useCallback(
    async (id: string, taskId?: string): Promise<CommentEntry[]> => {
      const res = await client.apiV1ManagementTicketCommentsList(
        undefined,
        "-created_at",
        UNPAGED_LIMIT,
        undefined,
        taskId ? undefined : id,
        taskId
      );
      return (res.data.results ?? []).map((r) => ({
        id: r.id,
        content: r.content,
        user: r.user as CommentAuthor,
        reply_to: r.reply_to as CommentAuthor | undefined,
        created_at: r.created_at,
        ticket: r.ticket,
        ticket_item: r.ticket_item,
        file_relations: r.file_relations,
        reactions: r.reactions,
      }));
    },
    []
  );

  const commentCreate = useCallback(
    async (
      ticketId: string,
      content: string,
      userId: number,
      replyTo?: number,
      ticketItem?: string,
      newFiles?: CommentNewFiles
    ): Promise<void> => {
      await client.apiV1ManagementTicketCommentsCreate({
        content,
        ticket: ticketId,
        user: userId,
        reply_to: replyTo,
        ticket_item: ticketItem ?? null,
        new_files: newFiles,
      });
      if (newFiles && Object.keys(newFiles).length > 0) {
        dispatch!(commentAttachmentsLinked(ticketId));
      }
    },
    [dispatch]
  );

  const commentCreateWithTaskSelector = useCallback(
    async (
      ticketId: string,
      content: string,
      userId: number,
      replyTo?: number,
      ticketItem?: string,
      newFiles?: CommentNewFiles
    ): Promise<void> => {
      await commentCreate(
        ticketId,
        content,
        userId,
        replyTo,
        replyTo === undefined ? (ticketItem ?? selectedTaskId) : ticketItem,
        newFiles
      );
    },
    [commentCreate, selectedTaskId]
  );

  const commentAttachmentUpload = useCallback(
    (file: File) => uploadCommentAttachment(dispatch!, file),
    [dispatch]
  );

  const commentFileAttach = useCallback(
    (file: File, target: CommentAttachmentTarget) =>
      attachFileToComment(dispatch!, { file, ...target }),
    [dispatch]
  );

  const commentReactionAdd = useCallback(
    async (
      commentId: string,
      emoji: EmojiEnum,
      userId: number
    ): Promise<ReactionMutationResult> => {
      const response = await client.apiV1ManagementTicketCommentReactionsCreate({
        comment: commentId,
        emoji,
        user: userId,
      });
      return isAxiosError(response)
        ? { succeeded: false, status: response.status }
        : { succeeded: true, reactionId: response.data.id };
    },
    []
  );

  const commentReactionRemove = useCallback(
    async (reactionId: string): Promise<ReactionMutationResult> => {
      const response = await client.apiV1ManagementTicketCommentReactionsDestroy(reactionId);
      return isAxiosError(response)
        ? { succeeded: false, status: response.status }
        : { succeeded: true };
    },
    []
  );

  const taskMap = useMemo(
    (): Record<string, TaskSummary> =>
      Object.fromEntries(
        (ticket.items ?? [])
          .filter((item) => item.id)
          .map((item) => [
            item.id as string,
            {
              number: item.ticket_item_number ?? 0,
              componentLabel: item.component ? formatComponentLabel(item.component) : undefined,
            },
          ])
      ),
    [ticket.items]
  );

  const taskSelectOptions = useMemo(
    () =>
      (ticket.items ?? []).map((item) => ({
        value: item.id,
        label: `Task #${item.ticket_item_number}${
          item.component ? ` — ${formatComponentLabel(item.component)}` : ""
        }`,
      })),
    [ticket.items]
  );

  const commentUpdate = useCallback(
    async (id: string, content: string): Promise<void> => {
      await client.apiV1ManagementTicketCommentsPartialUpdate(id, { content });
    },
    []
  );

  const commentDelete = useCallback(async (id: string): Promise<void> => {
    await client.apiV1ManagementTicketCommentsDestroy(id);
  }, []);

  const worklogEntityMaps = useSelector(
    (state: RootState) => state.ticket.worklogEntityMaps
  );

  const logContext = useMemo(
    () =>
      buildLogContext(
        ticket,
        ticketData.materialCatalog,
        ticketData.serviceObjects,
        worklogEntityMaps?.location ?? {},
        worklogEntityMaps?.turbine ?? {},
        worklogEntityMaps?.client ?? {},
        worklogEntityMaps?.project ?? {}
      ),
    [ticket, ticketData.materialCatalog, ticketData.serviceObjects, worklogEntityMaps]
  );

  const woContext = useMemo(() => buildWOClipboardContext(ticket), [ticket]);

  const tabItems = [
    {
      key: "comments",
      label: "Comments",
      children: (
        <Flex vertical>
          <Select
            style={{ width: "100%", marginBottom: 8 }}
            placeholder="Link next comment to a task (optional)"
            allowClear
            options={taskSelectOptions}
            value={selectedTaskId}
            onChange={setSelectedTaskId}
          />
          <CommentsTab
            ticketId={ticket.id!}
            currentUserId={currentUserId}
            taskMap={taskMap}
            onList={commentList}
            onCreate={commentCreateWithTaskSelector}
            onUpdate={commentUpdate}
            onDelete={commentDelete}
            onUploadAttachment={commentAttachmentUpload}
            onAttachFileToComment={commentFileAttach}
            onAddReaction={commentReactionAdd}
            onRemoveReaction={commentReactionRemove}
            getDownloadUrl={buildDownloadUrl}
            intl={intl}
          />
        </Flex>
      ),
    },
    {
      key: "files",
      label: intl.formatMessage({ id: "ticket_tab_files" }),
      children: (
        <FilesInfo
          intl={intl}
          files={files}
          dispatch={dispatch!}
          ticketId={ticket.id!}
          items={ticket.items}
          fileRelations={ticket.file_relations}
        />
      ),
    },
    {
      key: "work_log",
      label: intl.formatMessage({ id: "ticket_tab_check_in_out_log" }),
      children: (
        <TimeSessionsInfo
          intl={intl}
          ticketId={ticket.id!}
          ticketStatus={ticket.status}
          woContext={woContext}
        />
      ),
    },
    {
      key: "standby",
      label: intl.formatMessage({ id: "ticket_tab_standby" }),
      children: (
        <StandbyPeriodsInfo
          intl={intl}
          ticketId={ticket.id!}
          totalStandbySeconds={ticket.total_standby_time}
        />
      ),
    },
    {
      key: "task_log",
      label: intl.formatMessage({ id: "ticket_tab_task_log" }),
      children: (
        <TaskLogInfo
          intl={intl}
          ticketId={ticket.id!}
          ticketStatus={ticket.status}
          taskMap={taskMap}
        />
      ),
    },
    {
      key: "wo_history",
      label: intl.formatMessage({ id: "ticket_tab_wo_history" }),
      children: <WorkLogInfo intl={intl} ticketId={ticket.id!} logContext={logContext} />,
    },
    {
      key: "supplies",
      label: intl.formatMessage({ id: "ticket_tab_supplies" }),
      children: (
        <SuppliesInfo
          intl={intl}
          materials={ticket.materials}
          materialCatalog={ticketData.materialCatalog}
          sapCodeCatalog={ticketData.sapCodeCatalog}
        />
      ),
    },
  ];

  return (
    <Flex vertical gap="middle">
      <TicketInfo
        currentRoute={null}
        intl={intl}
        ticketData={ticketData}
        modalApi={modalApi}
        dispatch={dispatch}
      />
      <TasksInfo
        intl={intl}
        currentRoute={currentRoute}
        ticketData={ticketData}
        modalApi={modalApi}
        dispatch={dispatch!}
      />
      <Card ref={tabsCardRef}>
        <Tabs items={tabItems} activeKey={activeTab} onChange={setActiveTab} />
      </Card>
    </Flex>
  );
};
